import React, { useState, useEffect } from "react";
import { Check, Minus, Plus, Building2, Zap } from "lucide-react";

// ─── Static plan feature definitions ────────────────────────────────────────
const PLAN_FEATURES = {
    Free: [
        "5 Forms",
        "Custom form builder",
        "1 user",
        "100 Maximum monthly response limit",
        "15MB receive file uploads",
        "1 template",
        "500 MB Available Space",
    ],
    Basic: [
        "50 Forms",
        "Custom form builder",
        "1 user",
        "750 Maximum monthly response limit",
        "1GB receive file uploads",
        "10 templates",
        "10GB Available Space",
        "Ensure GDPR Compliance",
    ],
    // Business: [
    //     "Unlimited forms",
    //     "Custom form builder",
    //     "20 users",
    //     "1,000 Maximum monthly response limit",
    //     "2GB receive file uploads",
    //     "25 templates",
    //     "Unlimited Available Space",
    //     "Create interactive forms that connect to your workflow",
    //     "Design and manage workflow",
    //     "Doc Sign with 2FA",
    //     "Ensure GDPR Compliance",
    //     "See user login activity",
    //     "Assign user roles and permissions",
    //     "On-screen sign",
    // ],
    // BusinessPlus: [
    //     "Unlimited forms",
    //     "Custom form builder",
    //     "Unlimited users",
    //     "Unlimited Maximum monthly response limit",
    //     "4GB receive file uploads",
    //     "25 templates",
    //     "Unlimited Available Space",
    //     "Create interactive forms that connect to your workflow",
    //     "Design and manage workflows",
    //     "Doc Sign with 2FA",
    //     "Ensure GDPR Compliance",
    //     "See user login activity",
    //     "Assign user roles and permissions",
    //     "On-screen sign",
    // ],
    // Enterprise: [],
};

const PLAN_DISPLAY_NAMES = {
    Free: "Free",
    Basic: "Basic",
    // Business: "Business",
    // BusinessPlus: "Business Plus",
    // Enterprise: "Enterprise",
};

const PLAN_ORDER = ["Free", "Basic",]; // "Business", "BusinessPlus", "Enterprise"

// Countries list (abbreviated — add more as needed)
const COUNTRIES = [
    "Nigeria", "United States", "United Kingdom", "Canada", "Germany",
    "France", "South Africa", "Ghana", "Kenya", "India", "Australia",
    "Brazil", "China", "Japan", "UAE", "Saudi Arabia", "Egypt",
];

// Currency symbol map by country
const COUNTRY_CURRENCY = {
    Nigeria: { symbol: "₦", key: "NGN" },
    "United States": { symbol: "$", key: "USD" },
    "United Kingdom": { symbol: "£", key: "USD" }, // fallback to USD for display
};
const DEFAULT_CURRENCY = { symbol: "$", key: "USD" };

function getCurrency(country) {
    return COUNTRY_CURRENCY[country] || DEFAULT_CURRENCY;
}

// ─── Sub-component: Plan Card ────────────────────────────────────────────────
function PlanCard({ plan, priceData, billingPeriod, isSelected, onSelect, currency }) {
    console.log("priceData", priceData);
    const [showAll, setShowAll] = useState(false);
    const features = PLAN_FEATURES[plan] || [];
    const visibleFeatures = showAll ? features : features.slice(0, 5);
    const isEnterprise = plan === "Enterprise";
    const isFree = plan === "Free";

    const price = priceData ? priceData[currency.key] : null;
    const hasPrice = price !== null && price !== undefined;
    const isFreePrice = hasPrice && price === 0;

    const formatPrice = (val) =>
        val === 0 ? "0" : val?.toLocaleString() ?? "—";

    return (
        <div
            className={`card h-100 position-relative ${isSelected ? "border-primary shadow" : "border"}`}
            style={{
                borderWidth: isSelected ? 2 : 1,
                cursor: isEnterprise ? "default" : "pointer",
                transition: "box-shadow 0.2s, border-color 0.2s",
                borderRadius: 12,
                background: isSelected ? "#f0f6ff" : "#fff",
            }}
            onClick={() => !isEnterprise && onSelect(plan)}
        >
            {isSelected && !isEnterprise && (
                <div
                    className="position-absolute top-0 start-50 translate-middle badge bg-primary"
                    style={{ fontSize: 11, borderRadius: 20, padding: "4px 12px" }}
                >
                    Selected
                </div>
            )}
            <div className="card-body d-flex flex-column p-3">
                {/* Plan name */}
                <h6 className="fw-bold mb-1" style={{ color: "#1a1a2e" }}>
                    {PLAN_DISPLAY_NAMES[plan]}
                </h6>

                {/* Price */}
                {isEnterprise ? (
                    <div className="mb-2">
                        <span className="display-6 fw-bold text-dark">₦0</span>
                        <span className="text-muted small ms-1">/ User / Year</span>
                    </div>
                ) : isFreePrice || isFree ? (
                    <div className="mb-2">
                        <span className="display-6 fw-bold text-dark">
                            {currency.symbol}0
                        </span>
                        <span className="text-muted small ms-1">/ User / {billingPeriod === "monthly" ? "Month" : "Year"}</span>
                    </div>
                ) : hasPrice ? (
                    <div className="mb-2">
                        <span className="fw-bold" style={{ fontSize: "1.6rem", color: "#1a1a2e" }}>
                            {currency.symbol}{formatPrice(price)}
                        </span>
                        <span className="text-muted small ms-1">
                            / User / {billingPeriod === "monthly" ? "Month" : "Year"}
                        </span>
                    </div>
                ) : (
                    <div className="mb-2 text-muted small fst-italic">Price loading…</div>
                )}

                {/* Features */}
                {isEnterprise ? (
                    <p className="text-muted small mb-3">
                        Unlock limitless growth with advanced features and support
                    </p>
                ) : (
                    <>
                        <ul className="list-unstyled mb-0 flex-grow-1" style={{ fontSize: 13 }}>
                            {visibleFeatures.map((f) => (
                                <li key={f} className="d-flex align-items-start gap-2 mb-1">
                                    <Check size={14} className="text-primary mt-1 flex-shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        {features.length > 5 && (
                            <button
                                className="btn btn-link btn-sm p-0 mt-1 text-primary"
                                style={{ fontSize: 12 }}
                                onClick={(e) => { e.stopPropagation(); setShowAll((v) => !v); }}
                            >
                                {showAll ? "Show Less ▲" : `Show More ▼`}
                            </button>
                        )}
                    </>
                )}

                {/* CTA */}
                <div className="mt-3">
                    {isEnterprise ? (
                        <button className="btn btn-outline-secondary w-100 rounded-pill btn-sm">
                            Contact Us
                        </button>
                    ) : (
                        <>
                            <button
                                className={`btn w-100 rounded-pill btn-sm ${isSelected ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={(e) => { e.stopPropagation(); onSelect(plan); }}
                            >
                                {isSelected ? "Selected" : "Get started"}
                            </button>
                            {!isFreePrice && !isFree && (
                                <p className="text-center text-muted mt-1 mb-0" style={{ fontSize: 11 }}>
                                    Start a free 14-day trial
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Sub-component: Plan Customiser ─────────────────────────────────────────
function PlanCustomiser({ plan, priceData, billingPeriod, onBillingChange, onResult, currency, country, onCountryChange }) {
    const [numUsers, setNumUsers] = useState(1);
    const isEnterprise = plan === "Enterprise";
    const isFree = plan === "Free" || (priceData && priceData[currency.key] === 0);

    const monthlyPrice = priceData?.monthly?.[currency.key] ?? 0;
    const yearlyPrice = priceData?.yearly?.[currency.key] ?? 0;

    const unitPrice = billingPeriod === "monthly" ? monthlyPrice : yearlyPrice;
    const subTotal = isFree ? 0 : unitPrice * numUsers;

    const maxUsers = plan === "BusinessPlus" || plan === "Enterprise" ? 999 : 20;

    const handleConfirm = () => {
        onResult({
            plan,
            displayName: PLAN_DISPLAY_NAMES[plan],
            billingPeriod,
            numUsers: isFree ? 1 : numUsers,
            country,
            currency: currency.key,
            unitPrice,
            subTotal,
            priceId: priceData?.[billingPeriod]?.id,
        });
    };

    return (
        <div className="row g-3 mt-2">
            {/* Left: customiser form */}
            <div className="col-12 col-md-7">
                <div className="card border rounded-3 p-4 h-100">
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Your business location</label>
                        <select
                            className="form-select"
                            value={country}
                            onChange={(e) => onCountryChange(e.target.value)}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Your subscription</label>
                        <div
                            className="rounded-3 p-3 d-flex justify-content-between align-items-center text-white fw-bold"
                            style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)" }}
                        >
                            <span>{PLAN_DISPLAY_NAMES[plan]}</span>
                            {!isEnterprise && (
                                <span style={{ fontSize: "1.3rem" }}>
                                    {currency.symbol}
                                    {(unitPrice ?? 0).toLocaleString()}
                                    <span className="fw-normal" style={{ fontSize: 13 }}>
                                        {" "}/ {billingPeriod === "monthly" ? "Month" : "Year"}
                                    </span>
                                </span>
                            )}
                            {isEnterprise && <span>Custom pricing</span>}
                        </div>
                    </div>

                    {!isEnterprise && (
                        <>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Billing period</label>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <div
                                            className={`border rounded-3 p-3 text-center ${billingPeriod === "monthly" ? "border-primary " : ""}`}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => onBillingChange("monthly")}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    type="radio"
                                                    checked={billingPeriod === "monthly"}
                                                    readOnly
                                                    className="form-check-input mt-0"
                                                />
                                                <div className="text-start">
                                                    <div className="fw-semibold" style={{ fontSize: 14 }}>Monthly</div>
                                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                                        {currency.symbol}{(monthlyPrice ?? 0).toLocaleString()} / User / Month
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div
                                            className={`border rounded-3 p-3 text-center ${billingPeriod === "yearly" ? "border-primary" : ""}`}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => onBillingChange("yearly")}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    type="radio"
                                                    checked={billingPeriod === "yearly"}
                                                    readOnly
                                                    className="form-check-input mt-0"
                                                />
                                                <div className="text-start">
                                                    <div className="fw-semibold" style={{ fontSize: 14 }}>Yearly</div>
                                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                                        {currency.symbol}{(yearlyPrice ?? 0).toLocaleString()} / User / Year
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!isFree && (
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Number of Users</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            className="btn btn-outline-primary"
                                            style={{ width: 48, height: 48 }}
                                            onClick={() => setNumUsers((n) => Math.max(1, n - 1))}
                                        >
                                            <Minus size={16} />
                                        </button>
                                        <input
                                            type="number"
                                            className="form-control text-center fw-bold"
                                            style={{ height: 48, fontSize: 18, maxWidth: 100 }}
                                            min={1}
                                            max={maxUsers}
                                            value={numUsers}
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value) || 1;
                                                setNumUsers(Math.min(maxUsers, Math.max(1, v)));
                                            }}
                                        />
                                        <button
                                            className="btn btn-outline-primary"
                                            style={{ width: 48, height: 48 }}
                                            onClick={() => setNumUsers((n) => Math.min(maxUsers, n + 1))}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="text-end mt-2">
                                <span className="text-muted me-2" style={{ fontSize: 14 }}>Sub Total</span>
                                <span className="fw-bold" style={{ fontSize: "1.5rem", color: "#1a1a2e" }}>
                                    {currency.symbol}{subTotal.toLocaleString()}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Right: features recap */}
            <div className="col-12 col-md-5">
                <div className="card border rounded-3 p-4 h-100 d-flex flex-column">
                    <h6 className="fw-bold mb-3">{PLAN_DISPLAY_NAMES[plan]} Plan Features</h6>
                    {isEnterprise ? (
                        <p className="text-muted">Unlock limitless growth with advanced features and support. Contact us for custom pricing.</p>
                    ) : (
                        <ul className="list-unstyled flex-grow-1" style={{ fontSize: 13 }}>
                            {(PLAN_FEATURES[plan] || []).map((f) => (
                                <li key={f} className="d-flex align-items-start gap-2 mb-2">
                                    <Check size={14} className="text-primary mt-1 flex-shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button
                        className="btn btn-primary rounded-pill mt-3"
                        onClick={handleConfirm}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────
/**
 * PricingPlanSelector
 *
 * Props:
 *   product      {string}   – filter plans by product key (e.g. "incless"). Defaults to "incless".
 *   apiBase      {string}   – base URL for the prices endpoint. Defaults to "".
 *   onConfirm    {function} – called with the structured result object when user clicks Continue.
 */
export default function InvexPricingModal({ product = "incless", apiBase = "", onConfirm }) {
    const [billingPeriod, setBillingPeriod] = useState("yearly");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [country, setCountry] = useState("Nigeria");
    const [prices, setPrices] = useState(null); // { PlanName: { monthly: {id,NGN,USD}, yearly: {…} } }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const currency = getCurrency(country);

    // Fetch & transform prices
    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`${apiBase}/auth/invex-plans/prices`)
            .then((r) => r.json())
            .then((data) => {
                if (!data.success) throw new Error("Failed to load plans");
                console.log("invex pricing data", data);

                // Filter by product and build lookup: { planName: { monthly: {...}, yearly: {...} } }
                const map = {};
                (data.invexPlans || [])
                    .filter((p) => p.product === product)
                    .forEach((p) => {
                        if (!map[p.name]) map[p.name] = {};
                        map[p.name][p.type] = { id: p.id, NGN: p.NGN, USD: p.USD };
                    });
                // Always ensure Enterprise exists (custom, no price)
                map.Enterprise = { monthly: { id: null, NGN: 0, USD: 0 }, yearly: { id: null, NGN: 0, USD: 0 } };
                setPrices(map);
                setLoading(false);
            })
            .catch((e) => {
                setError(e.message);
                setLoading(false);
            });
    }, [apiBase, product]);

    const getPriceForCard = (planKey) => {
        console.log("prices", prices);

        if (!prices) return null;
        const pd = prices[planKey];
        if (!pd) return null;
        return pd[billingPeriod]?.[currency.key] ?? null;
    };

    const getPlanPriceData = (planKey) => {
        console.log("prices", prices);
        if (!prices) return null;
        const pd = prices[planKey];
        if (!pd) return null;
        return {
            monthly: pd.monthly,
            yearly: pd.yearly,
            NGN: pd[billingPeriod]?.NGN,
            USD: pd[billingPeriod]?.USD,
            id: pd[billingPeriod]?.id,
        };
    };

    const handleResult = (data) => {
        setResult(data);
        onConfirm?.(data);
    };

    return (
        <div className="container-fluid py-4 px-3" style={{ maxWidth: 1200 }}>
            {/* Header toggle */}
            <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
                <span className={`fw-semibold ${billingPeriod === "monthly" ? "text-dark" : "text-muted"}`}>
                    Pay monthly
                </span>
                {/* Toggle switch */}
                <div
                    className="position-relative"
                    style={{
                        width: 52,
                        height: 28,
                        background: "#2563eb",
                        borderRadius: 14,
                        cursor: "pointer",
                    }}
                    onClick={() => setBillingPeriod((v) => v === "monthly" ? "yearly" : "monthly")}
                >
                    <div
                        style={{
                            position: "absolute",
                            top: 3,
                            left: billingPeriod === "yearly" ? 27 : 3,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#fff",
                            transition: "left 0.2s",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }}
                    />
                </div>
                <span className={`fw-semibold ${billingPeriod === "yearly" ? "text-dark" : "text-muted"}`}>
                    Pay yearly
                </span>
            </div>

            {/* Error / loading */}
            {error && (
                <div className="alert alert-warning text-center mb-3" style={{ fontSize: 14 }}>
                    Could not load prices: {error}. Prices will show once the API is reachable.
                </div>
            )}

            {/* Plan cards */}
            <div className="row g-3 align-items-stretch">
                {PLAN_ORDER.map((plan) => (
                    <div key={plan} className="col-12 col-sm-6 col-lg">
                        <PlanCard
                            plan={plan}
                            priceData={{ [currency.key]: getPriceForCard(plan) }}
                            billingPeriod={billingPeriod}
                            isSelected={selectedPlan === plan}
                            onSelect={setSelectedPlan}
                            currency={currency}
                        />
                    </div>
                ))}
            </div>

            {/* Customiser */}
            {selectedPlan && (
                <>
                    <hr className="my-4" />
                    <div className="d-flex align-items-center gap-3 mb-3">
                        {/* Breadcrumb-style stepper */}
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 32, height: 32, background: "#e2e8f0", fontSize: 14, fontWeight: 600 }}
                            >
                                ✓
                            </div>
                            <div>
                                <div className="fw-semibold" style={{ fontSize: 13 }}>Choose Plan</div>
                                <div className="text-primary" style={{ fontSize: 12 }}>{PLAN_DISPLAY_NAMES[selectedPlan]}</div>
                            </div>
                        </div>
                        <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                style={{ width: 32, height: 32, background: "#2563eb", fontSize: 14 }}
                            >
                                2
                            </div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>Customise</div>
                        </div>
                    </div>

                    <PlanCustomiser
                        plan={selectedPlan}
                        priceData={getPlanPriceData(selectedPlan)}
                        billingPeriod={billingPeriod}
                        onBillingChange={setBillingPeriod}
                        onResult={handleResult}
                        currency={currency}
                        country={country}
                        onCountryChange={setCountry}
                    />
                </>
            )}

            {/* Structured result preview */}
            {result && (
                <div className="mt-4">
                    <div className="alert border rounded-3 bg-light">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Zap size={16} className="text-primary" />
                            <strong style={{ fontSize: 14 }}>Plan Selection Result</strong>
                        </div>
                        <pre className="mb-0 text-dark" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * USAGE EXAMPLE
 * ─────────────
 * import PricingPlanSelector from "./PricingPlanSelector";
 *
 * function App() {
 *   return (
 *     <PricingPlanSelector
 *       product="incless"
 *       apiBase="https://your-api.com"
 *       onConfirm={(result) => {
 *         console.log("Plan selected:", result);
 *         // result shape:
 *         // {
 *         //   plan: "Business",
 *         //   displayName: "Business",
 *         //   billingPeriod: "yearly",
 *         //   numUsers: 3,
 *         //   country: "Nigeria",
 *         //   currency: "NGN",
 *         //   unitPrice: 125000,
 *         //   subTotal: 375000,
 *         //   priceId: 359,
 *         // }
 *       }}
 *     />
 *   );
 * }
 */